import { Link, useLocation } from "react-router-dom";
import { LogOut, MapPin, Package, ShieldCheck } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";

const GoogleG = () => (
  <svg viewBox="0 0 24 24" className="h-4 w-4" aria-hidden="true">
    <path fill="#EA4335" d="M12 10.2v3.9h5.4c-.2 1.3-1.6 3.9-5.4 3.9-3.3 0-5.9-2.7-5.9-6s2.6-6 5.9-6c1.9 0 3.1.8 3.8 1.5l2.6-2.5C16.8 3.4 14.6 2.4 12 2.4 6.7 2.4 2.4 6.7 2.4 12S6.7 21.6 12 21.6c5.5 0 9.2-3.9 9.2-9.4 0-.6-.1-1.1-.2-1.6H12z" />
  </svg>
);

export const SignInButton = ({ className = "", label = "Sign in", testId = "google-signin-button" }) => {
  const { login } = useAuth();
  const { pathname } = useLocation();
  return (
    <button onClick={() => login(pathname)} data-testid={testId} className={`inline-flex items-center gap-2 rounded-full border border-ragi/20 bg-white px-4 py-2 text-sm font-semibold text-ragi-dark transition-colors hover:border-ragi/40 hover:bg-cream-secondary ${className}`}>
      <GoogleG /> {label}
    </button>
  );
};

export default function UserMenu() {
  const { user, loading, logout } = useAuth();
  if (loading) return <span className="h-10 w-10 rounded-full bg-cream-muted/60" aria-hidden="true" />;
  if (!user) return <SignInButton className="hidden sm:inline-flex" />;

  const initials = user.name.split(" ").map((p) => p[0]).join("").slice(0, 2).toUpperCase();
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button className="inline-flex h-10 w-10 items-center justify-center overflow-hidden rounded-full border-2 border-saffron/60 bg-ragi text-xs font-bold text-cream transition-transform hover:scale-105" aria-label="Account menu" data-testid="user-menu-button">
          {user.picture ? <img src={user.picture} alt="" className="h-full w-full object-cover" referrerPolicy="no-referrer" /> : initials}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-60 bg-cream" data-testid="user-menu">
        <DropdownMenuLabel>
          <p className="truncate text-sm font-semibold text-ragi-dark" data-testid="user-menu-name">{user.name}</p>
          <p className="truncate text-xs font-normal text-muted-foreground" data-testid="user-menu-email">{user.email}</p>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem asChild><Link to="/account/orders" className="flex items-center gap-2" data-testid="user-menu-orders-link"><Package className="h-4 w-4" /> My Orders</Link></DropdownMenuItem>
        <DropdownMenuItem asChild><Link to="/rider" className="flex items-center gap-2" data-testid="user-menu-rider-link"><MapPin className="h-4 w-4" /> Rider deliveries</Link></DropdownMenuItem>
        {user.is_admin && (
          <DropdownMenuItem asChild><Link to="/admin" className="flex items-center gap-2" data-testid="user-menu-admin-link"><ShieldCheck className="h-4 w-4" /> Owner Dashboard</Link></DropdownMenuItem>
        )}
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={logout} className="flex items-center gap-2 text-terracotta" data-testid="user-menu-logout"><LogOut className="h-4 w-4" /> Sign out</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
